import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Tüm 11 dil için çeviriler
const translations = {
  en: {
    greeting: 'Hello!',
    message: 'Can I help you? I can answer your questions about your projects.',
    cta: 'Start Chat',
    dismiss: 'Not now'
  },
  tr: {
    greeting: 'Merhaba!',
    message: 'Size yardımcı olabilir miyim? Projeleriniz hakkında sorularınızı yanıtlayabilirim.',
    cta: 'Sohbet Başlat',
    dismiss: 'Şimdi değil'
  },
  es: {
    greeting: '¡Hola!',
    message: '¿Puedo ayudarte? Puedo responder tus preguntas sobre tus proyectos.',
    cta: 'Iniciar Chat',
    dismiss: 'Ahora no'
  },
  pt: {
    greeting: 'Olá!',
    message: 'Posso ajudá-lo? Posso responder suas perguntas sobre seus projetos.',
    cta: 'Iniciar Chat',
    dismiss: 'Agora não'
  },
  zh: {
    greeting: '您好！',
    message: '我能帮助您吗？我可以回答您关于项目的问题。',
    cta: '开始聊天',
    dismiss: '稍后再说'
  },
  ar: {
    greeting: 'مرحباً!',
    message: 'هل يمكنني مساعدتك؟ يمكنني الإجابة على أسئلتك حول مشاريعك.',
    cta: 'ابدأ المحادثة',
    dismiss: 'ليس الآن'
  },
  hi: {
    greeting: 'नमस्ते!',
    message: 'क्या मैं आपकी मदद कर सकता हूं? मैं आपके प्रोजेक्ट्स के बारे में आपके सवालों का जवाब दे सकता हूं।',
    cta: 'चैट शुरू करें',
    dismiss: 'अभी नहीं'
  },
  ko: {
    greeting: '안녕하세요!',
    message: '도움이 필요하신가요? 프로젝트에 관한 질문에 답변해 드릴 수 있습니다.',
    cta: '채팅 시작',
    dismiss: '나중에'
  },
  el: {
    greeting: 'Γεια σας!',
    message: 'Μπορώ να σας βοηθήσω; Μπορώ να απαντήσω στις ερωτήσεις σας για τα έργα σας.',
    cta: 'Έναρξη Συνομιλίας',
    dismiss: 'Όχι τώρα'
  },
  it: {
    greeting: 'Ciao!',
    message: 'Posso aiutarti? Posso rispondere alle tue domande sui tuoi progetti.',
    cta: 'Inizia Chat',
    dismiss: 'Non ora'
  },
  gu: {
    greeting: 'નમસ્તે!',
    message: 'શું હું તમને મદદ કરી શકું? હું તમારા પ્રોજેક્ટ્સ વિશે તમારા પ્રશ્નોના જવાબ આપી શકું છું.',
    cta: 'ચેટ શરૂ કરો',
    dismiss: 'હમણાં નહીં'
  }
};

const getContent = (lang) => translations[lang] || translations.en;

const LiveNotification = ({ onOpenChat, delay = 15000 }) => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const content = getContent(language);

  useEffect(() => {
    // Check if already dismissed this session
    const dismissed = sessionStorage.getItem('pironeNotificationDismissed');
    if (dismissed) {
      setIsDismissed(true);
      return;
    }

    // Show notification after delay
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    sessionStorage.setItem('pironeNotificationDismissed', 'true');
  };

  const handleOpenChat = () => {
    setIsVisible(false);
    setIsDismissed(true);
    sessionStorage.setItem('pironeNotificationDismissed', 'true');
    onOpenChat();
  };

  if (isDismissed) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-24 right-6 z-[90] w-[320px] max-w-[calc(100vw-48px)]"
        >
          <div 
            className="relative bg-[#0A0A0A] border border-white/[0.08] rounded-2xl overflow-hidden"
            style={{ 
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(56, 189, 248, 0.06)'
            }}
          >
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#38BDF8] via-[#0EA5E9] to-[#38BDF8]" />
            
            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="absolute top-3 right-3 w-6 h-6 rounded-full bg-white/[0.05] hover:bg-white/[0.1] flex items-center justify-center transition-all z-10"
            >
              <X size={12} className="text-white/40" />
            </button>

            {/* Content */}
            <div className="p-5">
              {/* Header with avatar */}
              <div className="flex items-start gap-3 mb-4">
                <div className="relative flex-shrink-0">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#38BDF8] to-[#0EA5E9] flex items-center justify-center shadow-lg shadow-[#38BDF8]/20">
                    <Sparkles size={22} className="text-white" />
                  </div>
                  {/* Online indicator with pulse */}
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-[#0A0A0A] flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                  </div>
                </div>
                
                <div className="flex-1 pt-1">
                  <p className="text-white font-semibold text-sm mb-0.5">{content.greeting}</p>
                  <p className="text-white/50 text-[13px] leading-relaxed">{content.message}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={handleOpenChat}
                  className="flex-1 flex items-center justify-center gap-2 h-10 rounded-xl bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] text-white text-sm font-semibold hover:shadow-lg hover:shadow-[#38BDF8]/20 transition-all"
                >
                  <MessageCircle size={14} />
                  {content.cta}
                </button>
                
                <button
                  onClick={handleDismiss}
                  className="px-4 h-10 rounded-xl bg-white/[0.03] border border-white/[0.08] text-white/50 text-sm font-medium hover:bg-white/[0.06] hover:text-white/70 transition-all"
                >
                  {content.dismiss}
                </button>
              </div>
            </div>

            {/* Subtle gradient background */}
            <div 
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at top right, rgba(56, 189, 248, 0.05) 0%, transparent 50%)'
              }}
            />
          </div>

          {/* Connector arrow */}
          <div className="absolute -bottom-2 right-8 w-4 h-4 bg-[#0A0A0A] border-r border-b border-white/[0.08] rotate-45" />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LiveNotification;
